/**
 * SeoManager
 *
 * Keeps <title>, meta description and the Open Graph / Twitter tags in the
 * document <head> in sync with the current route AND the currently-selected
 * language. Runs on every location or `i18n.language` change.
 *
 * Translation keys live under `seo.pages.*` in en.json / fr.json. A route
 * that isn't mapped falls back to the site default (`seo.defaultDescription`
 * + `seo.pages.home.title`), keeping social previews consistent.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CANONICAL_ORIGIN = "https://hepcure.ca";

// Route → SEO bucket. Anything not listed uses "home".
const ROUTE_KEY = {
  "/":         "home",
  "/about":    "about",
  "/programs": "programs",
  "/platform": "platform",
  "/partners": "partners",
  "/faq":      "faq",
  "/careers":  "careers",
  "/contact":  "contact",
  "/learn":    "learn",
  "/pharmacy": "pharmacy",
};

const upsertMeta = (selector, attr, value) => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [prop, val] = selector.replace(/[[\]"]/g, "").split("=");
    el.setAttribute(prop, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

export default function SeoManager() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const key = ROUTE_KEY[pathname] || "home";
    const title = t(`seo.pages.${key}.title`);
    const description =
      t(`seo.pages.${key}.description`) || t("seo.defaultDescription");
    const locale = i18n.language === "fr" ? "fr_CA" : "en_CA";
    const url = `${CANONICAL_ORIGIN}${pathname === "/" ? "" : pathname}`;

    document.title = title;
    upsertMeta('meta[name="description"]', "content", description);
    upsertMeta('meta[property="og:title"]', "content", title);
    upsertMeta('meta[property="og:description"]', "content", description);
    upsertMeta('meta[property="og:locale"]', "content", locale);
    upsertMeta('meta[property="og:url"]', "content", url);
    upsertMeta('meta[name="twitter:title"]', "content", title);
    upsertMeta('meta[name="twitter:description"]', "content", description);
  }, [pathname, i18n.language, t]);

  return null;
}
